import React from 'react';
import styled from 'astroturf/react';

import AuthorList from 'js/canonical_cards/shared/author_list';
import ResearchInterestList from 'js/canonical_cards/work_card/research_interest_list';
import showModal from 'js/components/show_modal';
import $image_url from 'js/utils/image_url';
import commaizeInt from 'js/utils/commaize';
import pluralize from 'pluralize';
import routes from 'js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';

import { Video } from '../shared/video_helper';
import { VideoPlayer } from '../shared/player/VideoPlayer';
import CustomVideoPlayer from '../shared/player/CustomVideoPlayer.wjs';
import VideoUnavailable from './VideoUnavailable';
import VideoReportModal from './VideoReportModal';

interface Props {
  video: Video;
  videoSeekTime: number | null;
  playerRef: React.MutableRefObject<VideoPlayer | null>;
  showReportButton: boolean;
  videoOverlay?: React.ReactNode;
  lockAfterSeconds?: number;
  onLock?: (unlockToTimestamp: number) => void;
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  background-color: #fff;
  color: #333;

  @media screen and (min-width: 993px) and (max-width: 1200px) {
    width: calc(100% - 395px);
  }

  @media screen and (min-width: 1201px) {
    width: calc(100% - 460px);
  }

  @media screen and (orientation: landscape) and (max-height: 780px) and (max-width: 1025px) {
    position: absolute;
    height: auto;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 5;
  }
`;

const ContentContainer = styled.div`
  padding: 10px 10px 30px 10px;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 630px) {
    padding: 40px;
    margin: auto;
  }

  @media screen and (min-width: 1200px) {
    max-width: 1150px;
  }

  @media screen and (orientation: landscape) and (max-height: 780px) and (max-width: 1025px) {
    padding: 0px 0px 30px 0px;
  }
`;

const LandscapePadding = styled.div`
  @media screen and (orientation: landscape) and (max-height: 780px) and (max-width: 1025px) {
    padding: 0px 10px;
  }
`;

const VideoContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 15px;
  max-width: 100%;
  height: auto;
  position: relative;

  @media screen and (min-width: 630px) {
    margin-bottom: 25px;
  }

  @media screen and (orientation: landscape) and (max-height: 780px) and (max-width: 1025px) {
    order: -1;
    margin-top: 0px;
    margin-bottom: 20px;
    width: 100vw;
    max-height: 100vh;
  }
`;

const Title = styled.h1`
  margin-bottom: 16px;
  margin-top: 0px;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: -0.54px;
  font-family: Georgia, Times, 'Times New Roman', serif;
  font-weight: normal;

  @media screen and (min-width: 630px) {
    font-size: 34px;
    line-height: 47px;
  }
`;

const LandscapeSeparator = styled.hr`
  display: none;

  @media screen and (orientation: landscape) and (max-height: 780px) and (max-width: 1025px) {
    display: block;
    margin: 15px 0px;
  }
`;

const MoreOptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 4px;
  align-items: center;
`;

const ViewsContainer = styled.div`
  height: 20px;
  font-size: 12px;
  margin-right: 20px;

  i {
    font-size: 11px;
    margin-right: 4px;
  }
`;

const ReportButtonContainer = styled.button`
  padding: 1px 10px;
  width: 80px;
  height: 25px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 2px;
  background-color: #fff;
  cursor: pointer;

  &:hover {
    background-color: #d0d0d0;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.18);
  }

  img {
    margin-right: 4px;
    margin-bottom: 2px;
  }
`;

const DescriptionHeader = styled.h2`
  margin: 0px;
  font-weight: 500;
  font-size: 18px;
  line-height: 36px;
`;

const Description = styled.p`
  font-size: 13px;
  line-height: 21px;
  margin-bottom: 25px;
  white-space: pre-wrap;
`;

const VideoPlayerMainContent: React.FunctionComponent<Props> = ({
  video,
  videoSeekTime,
  showReportButton,
  videoOverlay,
  lockAfterSeconds,
  onLock,
}) => {
  const {
    id,
    title,
    owner,
    museVideo,
    description,
    researchInterests,
    processingStatus,
  } = video;

  const logVideoWatched = () => {
    fetchWithCsrf(
      routes.api_v0_video_log_watch_path({
        video_id: id,
        subdomain_param: 'api',
      }),
      {
        method: 'POST',
      },
    );
  };

  let videoContent: React.ReactNode;

  if (video.hlsPlaylistUrl) {
    const videoJsOptions = {
      controls: true,
      autoplay: true,
      fluid: true,
      muted: false,
      responsive: true,
      preload: 'auto',
      playbackRates: [0.5, 1.0, 1.5, 2.0],
      controlBar: {
        volumePanel: {
          inline: false,
        },
        skipButtons: {
          forward: 30,
          backward: 10,
        },
      },
      userActions: {
        click: true,
        doubleClick: true,
        hotkeys: true,
      },
      html5: {
        hls: {
          overrideNative: true,
        },
      },
      sources: [
        {
          src: video.hlsPlaylistUrl,
          type: 'application/x-mpegURL',
        },
      ],
    };

    videoContent = (
      <CustomVideoPlayer
        options={videoJsOptions}
        lockAfterSeconds={lockAfterSeconds}
        onLock={onLock}
        seekTime={videoSeekTime || undefined}
        logger={{
          logWatch: logVideoWatched,
        }}
      />
    );
  } else {
    videoContent = <VideoUnavailable processingStatus={processingStatus} />;
  }

  return (
    <>
      <Container>
        <ContentContainer>
          <LandscapePadding>
            <Title>{title}</Title>
            <AuthorList
              authors={[owner]}
              enablePopover={false}
              addClickTracking={(url) => url}
            />
            <LandscapeSeparator />
          </LandscapePadding>

          <VideoContainer>
            {videoOverlay}
            {videoContent}
          </VideoContainer>

          <LandscapePadding>
            <MoreOptionsContainer>
              {museVideo && (
                <ViewsContainer>
                  <i className="fa fa-eye" />
                  {`${commaizeInt(video.viewCount)} ${pluralize(
                    'view',
                    video.viewCount,
                  )}`}
                </ViewsContainer>
              )}
              {showReportButton && (
                <ReportButtonContainer
                  onClick={() =>
                    showModal(VideoReportModal, { videoId: video.id })
                  }
                >
                  <img src={$image_url('flag.svg')} />
                  Report
                </ReportButtonContainer>
              )}
            </MoreOptionsContainer>
            <DescriptionHeader>Description</DescriptionHeader>
            <Description>{description}</Description>
            <ResearchInterestList
              interests={researchInterests}
              enablePopover={false}
            />
          </LandscapePadding>
        </ContentContainer>
      </Container>
    </>
  );
};

export default VideoPlayerMainContent;
